import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { 
  DollarSign, 
  CreditCard, 
  Building2, 
  TrendingUp, 
  TrendingDown, 
  Plus, 
  Search, 
  Filter,
  Edit,
  Eye,
  Download,
  AlertCircle,
  CheckCircle,
  Calendar
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useNavigate } from 'react-router-dom';

interface BankAccount {
  id: string;
  name: string;
  type: string;
  accountNumber: string;
  balance: number;
  currency: string;
  status: 'active' | 'inactive' | 'frozen';
}

interface CreditCard {
  id: string;
  name: string;
  cardNumber: string;
  creditLimit: number;
  currentBalance: number;
  dueDate: string;
  status: 'active' | 'inactive' | 'overdue';
}

interface Transaction {
  id: string;
  description: string;
  amount: number;
  type: 'income' | 'expense';
  category: string;
  date: string;
  account: string;
}

export function MobileFinancialDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'accounts' | 'cards' | 'transactions'>('overview');
  const [searchQuery, setSearchQuery] = useState('');

  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [creditCards, setCreditCards] = useState<CreditCard[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadFinancialData();
    }
  }, [user]);

  const loadFinancialData = async () => {
    try {
      setLoading(true);
      const [accountsResponse, transactionsResponse] = await Promise.all([
        supabase
          .from('financial_accounts')
          .select('*')
          .eq('user_id', user?.id),
        supabase
          .from('financial_transactions')
          .select('*')
          .eq('user_id', user?.id)
          .order('date', { ascending: false })
          .limit(20)
      ]);

      // Transform database accounts to expected format
      const accounts = accountsResponse.data?.map(acc => ({
        id: acc.id,
        name: acc.name,
        type: acc.type,
        accountNumber: acc.account_number ? `****${acc.account_number.slice(-4)}` : '****0000',
        balance: parseFloat(acc.balance || '0'),
        currency: acc.currency || 'USD',
        status: acc.status || 'active'
      })) || [];

      // Transform database transactions to expected format
      const trans = transactionsResponse.data?.map(t => ({
        id: t.id,
        description: t.description,
        amount: Math.abs(parseFloat(t.amount || '0')),
        type: t.transaction_type === 'deposit' ? 'income' : 'expense',
        category: t.category || 'Other',
        date: t.date,
        account: accounts.find(acc => acc.id === t.account_id)?.name || 'Unknown Account'
      })) || [];

      setBankAccounts(accounts.filter(acc => acc.type !== 'credit_card'));
      setCreditCards(accounts.filter(acc => acc.type === 'credit_card'));
      setTransactions(trans);
    } catch (error) {
      console.error('Error loading financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'inactive':
        return 'text-gray-600 bg-gray-100';
      case 'frozen':
      case 'overdue':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const totalBalance = bankAccounts.reduce((sum, account) => sum + account.balance, 0);
  const totalCreditLimit = creditCards.reduce((sum, card) => sum + card.creditLimit, 0);
  const totalCreditUsed = creditCards.reduce((sum, card) => sum + card.currentBalance, 0);
  const creditUtilization = (totalCreditUsed / totalCreditLimit) * 100;

  const renderOverview = () => (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign size={16} className="text-green-500" />
            <h3 className="font-medium text-gray-900">Total Balance</h3>
          </div>
          <p className="text-2xl font-bold text-gray-900">${totalBalance.toLocaleString()}</p>
          <p className="text-xs text-gray-500">Across {bankAccounts.length} accounts</p>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard size={16} className="text-blue-500" />
            <h3 className="font-medium text-gray-900">Credit Used</h3>
          </div>
          <p className="text-2xl font-bold text-gray-900">{creditUtilization.toFixed(1)}%</p>
          <p className="text-xs text-gray-500">${totalCreditUsed.toLocaleString()} / ${totalCreditLimit.toLocaleString()}</p>
        </Card>
      </div>

      {/* Recent Transactions */}
      <div>
        <h3 className="font-medium text-gray-900 mb-3">Recent Transactions</h3>
        <div className="space-y-2">
          {transactions.length > 0 ? (
            transactions.slice(0, 3).map((transaction) => (
              <div key={transaction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{transaction.description}</p>
                  <p className="text-sm text-gray-600">{transaction.category} • {new Date(transaction.date).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className={`font-medium ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                    {transaction.type === 'income' ? '+' : '-'}${transaction.amount.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">{transaction.account}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="flex flex-col items-center gap-3">
                <TrendingUp className="h-8 w-8 text-gray-300" />
                <div>
                  <p className="font-medium text-gray-900 mb-1">No Transactions Yet</p>
                  <p className="text-sm text-gray-500">Connect your accounts to see transactions</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button variant="outline" className="w-full">
          <Plus size={16} className="mr-2" />
          Add Transaction
        </Button>
        <Button variant="outline" className="w-full">
          <Download size={16} className="mr-2" />
          Export Data
        </Button>
      </div>
    </div>
  );

  const renderAccounts = () => (
    <div className="space-y-3">
      {bankAccounts.length > 0 ? (
        bankAccounts.map((account) => (
          <Card key={account.id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 size={16} className="text-blue-500" />
                  <h3 className="font-medium text-gray-900">{account.name}</h3>
                </div>
                <p className="text-sm text-gray-600">{account.type} • {account.accountNumber}</p>
                <p className="text-xs text-gray-500">{account.currency}</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-900">${account.balance.toLocaleString()}</p>
                <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(account.status)}`}>
                  {account.status}
                </span>
              </div>
            </div>
          </Card>
        ))
      ) : (
        <div className="text-center py-12 text-gray-500">
          <div className="flex flex-col items-center gap-4">
            <Building2 className="h-12 w-12 text-gray-300" />
            <div>
              <h3 className="font-medium text-gray-900 mb-2">No Bank Accounts</h3>
              <p className="text-sm text-gray-500 mb-4">
                Connect your bank accounts to manage your finances
              </p>
              <Button 
                variant="outline" 
                onClick={() => navigate('/dashboard/financial/accounts')}
                className="text-sm"
              >
                Add Bank Account
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderCards = () => (
    <div className="space-y-3">
      {creditCards.length > 0 ? (
        creditCards.map((card) => (
        <Card key={card.id} className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{card.name}</h3>
                <p className="text-sm text-gray-600">{card.cardNumber}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(card.status)}`}>
                {card.status}
              </span>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Balance:</span>
                <span className="font-medium text-gray-900">${card.currentBalance.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Credit Limit:</span>
                <span className="font-medium text-gray-900">${card.creditLimit.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Due Date:</span>
                <span className="font-medium text-gray-900">{card.dueDate}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1">
                <Eye size={14} className="mr-1" />
                View
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                <Edit size={14} className="mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </Card>
        ))
      ) : (
        <div className="text-center py-12 text-gray-500">
          <div className="flex flex-col items-center gap-4">
            <CreditCard className="h-12 w-12 text-gray-300" />
            <div>
              <h3 className="font-medium text-gray-900 mb-2">No Credit Cards</h3>
              <p className="text-sm text-gray-500 mb-4">
                Add your credit cards to track spending and payments
              </p>
              <Button 
                variant="outline" 
                onClick={() => navigate('/dashboard/financial/cards')}
                className="text-sm"
              >
                Add Credit Card
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderTransactions = () => (
    <div className="space-y-3">
      {transactions.length > 0 ? (
        transactions.map((transaction) => (
        <Card key={transaction.id} className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">{transaction.description}</h3>
              <p className="text-sm text-gray-600">{transaction.category} • {transaction.account}</p>
              <p className="text-xs text-gray-500">{transaction.date}</p>
            </div>
            <div className="text-right">
              <p className={`font-medium ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                {transaction.type === 'income' ? '+' : '-'}${transaction.amount}
              </p>
              <div className="flex gap-1 mt-2">
                <Button size="sm" variant="ghost">
                  <Eye size={14} />
                </Button>
                <Button size="sm" variant="ghost">
                  <Edit size={14} />
                </Button>
              </div>
            </div>
          </div>
        </Card>
        ))
      ) : (
        <div className="text-center py-12 text-gray-500">
          <div className="flex flex-col items-center gap-4">
            <TrendingUp className="h-12 w-12 text-gray-300" />
            <div>
              <h3 className="font-medium text-gray-900 mb-2">No Transactions Yet</h3>
              <p className="text-sm text-gray-500 mb-4">
                Start tracking your financial transactions
              </p>
              <Button 
                variant="outline" 
                onClick={() => navigate('/dashboard/financial/accounts')}
                className="text-sm"
              >
                Connect Account
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-4 pb-safe space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Financial Management</h2>
          <p className="text-sm text-gray-600">Track accounts, cards, and transactions</p>
        </div>
        <Button size="sm">
          <Plus size={16} className="mr-2" />
          Add Account
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search transactions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
        />
      </div>

      {/* Tab Navigation */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant={activeTab === 'overview' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('overview')}
        >
          <TrendingUp size={16} className="mr-2" />
          Overview
        </Button>
        <Button
          variant={activeTab === 'accounts' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('accounts')}
        >
          <Building2 size={16} className="mr-2" />
          Accounts
        </Button>
        <Button
          variant={activeTab === 'cards' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('cards')}
        >
          <CreditCard size={16} className="mr-2" />
          Cards
        </Button>
        <Button
          variant={activeTab === 'transactions' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('transactions')}
        >
          <DollarSign size={16} className="mr-2" />
          Transactions
        </Button>
      </div>

      {/* Content */}
      <div className="space-y-4">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'accounts' && renderAccounts()}
        {activeTab === 'cards' && renderCards()}
        {activeTab === 'transactions' && renderTransactions()}
      </div>

      {/* Alerts */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle size={16} className="text-yellow-600" />
          <h4 className="font-medium text-yellow-800">Financial Reminders</h4>
        </div>
        <div className="space-y-1 text-sm text-yellow-700">
          <p>• Credit card payment due in 3 days</p>
          <p>• Monthly budget review due</p>
        </div>
      </div>
    </div>
  );
} 